import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Alert, Spin, Badge, Statistic, Space, Typography,
  Select, Tag, Button, Modal, Radio, Progress, Divider, Tooltip, message, Empty
} from 'antd';
import {
  fetchDashboardFilterOptions,
  fetchDashboardAnalytics,
  fetchAllSessions,
  fetchUserWatchAnalytics
} from '../../../../Service/Dashboard_API/dashboardApi';
import {
  fetchDashboardScheduledAssessments,
  fetchAssessmentDetails,
  submitAssessmentAttempt,
  fetchAssessmentResults
} from '../../../../Service/Assessment_API/assessmentApi';
import {
  VideoCameraOutlined, BookOutlined, DesktopOutlined, ReadOutlined,
  FieldTimeOutlined, BarChartOutlined, PieChartOutlined, PlaySquareOutlined,
  FileTextOutlined, ClockCircleOutlined, TrophyOutlined, CheckCircleOutlined,
  CloseCircleOutlined, EyeOutlined, ThunderboltOutlined, RightOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import CourseWatchChart from '../../Modules/ModulesComponents/CourseWatchChart';
import { SERVER_URL } from '../../../../Service/apiConfig';

const { Text, Title, Paragraph } = Typography;
const { Option } = Select;

const UserDashboard = ({ onMenuSelect }) => {
  const [filters, setFilters] = useState({
    academic_year_id: 'all',
    institution_id: 'all',
    instructor_id: 'all',
    menu_key: 'all'
  });
  const [filterOptions, setFilterOptions] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [watchAnalytics, setWatchAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [liveSessions, setLiveSessions] = useState([]);
  const [isFilterLoaded, setIsFilterLoaded] = useState(false);

  // Scheduled Assessments State
  const [scheduledAssessments, setScheduledAssessments] = useState([]);
  const [loadingAssessments, setLoadingAssessments] = useState(false);

  // Interactive Quiz Player State
  const [playerVisible, setPlayerVisible] = useState(false);
  const [activeQuiz, setActiveQuiz] = useState(null);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [currentQIndex, setCurrentQIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [timeLeftSeconds, setTimeLeftSeconds] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);
  const [quizResult, setQuizResult] = useState(null);

  // Results Modal State
  const [resultsVisible, setResultsVisible] = useState(false);
  const [selectedAssessmentForResults, setSelectedAssessmentForResults] = useState(null);
  const [resultsList, setResultsList] = useState([]);
  const [loadingResults, setLoadingResults] = useState(false);

  // Fetch filter options on mount & default to current active Academic Year
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const res = await fetchDashboardFilterOptions();
        if (res.success) {
          setFilterOptions(res);
          if (res.academicYears && res.academicYears.length > 0) {
            const activeAy = res.academicYears.find(
              y => y.status?.toLowerCase() === 'active' || y.status === 'Active' || y.status === 'ACTIVE' || y.status === 1 || y.status === '1'
            ) || res.academicYears.find(y => {
              if (!y.start_date || !y.end_date) return false;
              const today = new Date();
              const start = new Date(y.start_date);
              const end = new Date(y.end_date);
              return !isNaN(start.getTime()) && !isNaN(end.getTime()) && today >= start && today <= end;
            }) || res.academicYears[0];

            if (activeAy) {
              const defaultAyId = String(activeAy.academic_year_id);
              setFilters((prev) => ({ ...prev, academic_year_id: defaultAyId }));
            }
          }
        }
      } catch (err) {
        console.error('Failed to load filter options:', err);
      } finally {
        setIsFilterLoaded(true);
      }
    };
    loadFilterOptions();
  }, []);

  const loadAnalytics = useCallback(async (isBackground = false) => {
    try {
      if (!isBackground) {
        setLoading(true);
        setError(null);
      }
      const [res, watchRes] = await Promise.all([
        fetchDashboardAnalytics(filters),
        fetchUserWatchAnalytics()
      ]);
      
      if (res && res.success) {
        setAnalytics(res);
      } else if (!isBackground) {
        setError(res?.message || 'Failed to load user analytics');
      }

      if (watchRes && watchRes.success) {
        setWatchAnalytics(watchRes);
      }
    } catch (err) {
      console.error('User analytics load error:', err);
      if (!isBackground) {
        setError(err.message || 'Error connecting to analytics');
      }
    } finally {
      if (!isBackground) {
        setLoading(false);
      }
    }
  }, [filters]);

  // Load Scheduled Assessments (appears 15 min prior to start time)
  const loadScheduledAssessments = useCallback(async () => {
    try {
      const res = await fetchDashboardScheduledAssessments();
      if (res && res.success) {
        setScheduledAssessments(res.assessments || []);
      }
    } catch (err) {
      console.error('Failed to load dashboard scheduled assessments:', err);
    }
  }, []);

  useEffect(() => {
    if (!isFilterLoaded) return;
    loadAnalytics();
    loadScheduledAssessments();
    const analyticsInterval = setInterval(() => {
      loadAnalytics(true);
      loadScheduledAssessments();
    }, 15000);
    return () => clearInterval(analyticsInterval);
  }, [loadAnalytics, loadScheduledAssessments, isFilterLoaded]);

  useEffect(() => {
    const fetchLive = async () => {
      try {
        const data = await fetchAllSessions();
        const sessionList = Array.isArray(data)
          ? data
          : (data && data.success && Array.isArray(data.sessions) ? data.sessions : (data && Array.isArray(data.sessions) ? data.sessions : []));
        setLiveSessions(sessionList);
      } catch (err) {
        console.error('Failed to fetch live sessions:', err);
      }
    };
    fetchLive();
    const interval = setInterval(fetchLive, 5000);
    return () => clearInterval(interval);
  }, []);

  // Handle Quiz Countdown Timer & Auto-Submit
  useEffect(() => {
    if (!playerVisible || quizResult) return;

    if (activeQuiz?.end_time) {
      const scheduledEnd = dayjs(activeQuiz.end_time);
      if (dayjs().isAfter(scheduledEnd)) {
        message.warning('Scheduled assessment end time reached! Submitting quiz automatically...');
        handleAutoSubmit();
        return;
      }
    }

    if (timeLeftSeconds === null || timeLeftSeconds <= 0) return;

    const timer = setInterval(() => {
      if (activeQuiz?.end_time && dayjs().isAfter(dayjs(activeQuiz.end_time))) {
        clearInterval(timer);
        message.warning('Scheduled assessment end time reached! Submitting quiz automatically...');
        handleAutoSubmit();
        return;
      }

      setTimeLeftSeconds(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          message.warning('Time limit reached! Submitting quiz automatically...');
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeftSeconds, playerVisible, quizResult, activeQuiz]);

  const handleStartQuiz = async (record) => {
    const now = dayjs();
    const start = record.start_time ? dayjs(record.start_time) : null;
    const end = record.end_time ? dayjs(record.end_time) : null;

    if (record.my_attempts_count > 0 && !record.allow_multiple_attempts) {
      message.warning('You have already submitted this assessment. Multiple attempts are not allowed.');
      return;
    }

    if (start && now.isBefore(start)) {
      message.warning(`Assessment has not started yet. Starts at ${start.format('DD MMM YYYY, hh:mm A')}`);
      return;
    }

    if (end && now.isAfter(end)) {
      message.warning(`Assessment schedule has expired. Ended at ${end.format('DD MMM YYYY, hh:mm A')}`);
      return;
    }

    try {
      setLoadingAssessments(true);
      const details = await fetchAssessmentDetails(record.assessment_id);
      const assessmentData = details.assessment || record;
      setActiveQuiz(assessmentData);
      setQuizQuestions(details.questions || []);
      setCurrentQIndex(0);
      setUserAnswers({});
      setQuizResult(null);

      const durMinutes = assessmentData?.duration_minutes || 0;
      let calculatedSeconds = null;

      if (durMinutes > 0) {
        calculatedSeconds = durMinutes * 60;
      }

      const schEnd = assessmentData?.end_time ? dayjs(assessmentData.end_time) : end;
      if (schEnd) {
        const secondsUntilEnd = Math.max(0, schEnd.diff(now, 'second'));
        if (calculatedSeconds !== null) {
          calculatedSeconds = Math.min(calculatedSeconds, secondsUntilEnd);
        } else {
          calculatedSeconds = secondsUntilEnd;
        }
      }

      setTimeLeftSeconds(calculatedSeconds);
      setPlayerVisible(true);
    } catch (err) {
      message.error('Failed to load quiz details');
    } finally {
      setLoadingAssessments(false);
    }
  };

  const handleFinalSubmitQuiz = async () => {
    if (!activeQuiz) return;
    setSubmittingQuiz(true);
    try {
      const res = await submitAssessmentAttempt(activeQuiz.assessment_id, userAnswers);
      if (res.success) {
        setQuizResult(res.result);
        message.success('Quiz submitted successfully!');
        loadScheduledAssessments();
      }
    } catch (err) {
      message.error(err.message || 'Failed to submit quiz');
    } finally {
      setSubmittingQuiz(false);
    }
  };

  const handleAutoSubmit = () => {
    handleFinalSubmitQuiz();
  };

  // Open Results Modal
  const handleOpenResults = async (record) => {
    setSelectedAssessmentForResults(record);
    setResultsVisible(true);
    setLoadingResults(true);
    try {
      const res = await fetchAssessmentResults(record.assessment_id);
      if (res.success) {
        setResultsList(res.attempts || []);
      }
    } catch (err) {
      message.error('Failed to fetch assessment results');
    } finally {
      setLoadingResults(false);
    }
  };

  const getFullImageUrl = (imgUrl) => {
    if (!imgUrl) return '';
    if (imgUrl.startsWith('http')) return imgUrl;
    return `${SERVER_URL}${imgUrl.startsWith('/') ? '' : '/'}${imgUrl}`;
  };

  const summary = analytics?.summary || {};
  const activeSessions = liveSessions.filter(
    (s) => s.status === 'live' || s.status === 'active' || s.rtmp_status === 'live' || s.rtmp_status === 'LIVE'
  );
  const activeCount = activeSessions.length;

  return (
    <div style={{ padding: '4px 0' }}>
      {error && <Alert message="Dashboard Note" description={error} type="warning" showIcon style={{ marginBottom: 20 }} />}

      {loading && !analytics ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" tip="Loading Student Dashboard..." />
        </div>
      ) : (
        <>
          {/* Executive Metrics Overview */}
          <Card
            title={
              <Text strong style={{ fontSize: 16, color: 'var(--text-color, #374151)' }}>
                Executive Metrics Overview
              </Text>
            }
            extra={
              <Space size="small">
                <Text strong style={{ fontSize: 13 }}>Academic Year:</Text>
                <Select showSearch optionFilterProp="children"
                  size="small"
                  value={filters.academic_year_id}
                  onChange={(val) => setFilters(prev => ({ ...prev, academic_year_id: val }))}
                  style={{ width: 140 }}
                  popupMatchSelectWidth={false}
                >
                  <Option value="all">All Academic Years</Option>
                  {(filterOptions?.academicYears || []).map((ay) => (
                    <Option key={ay.academic_year_id} value={String(ay.academic_year_id)}>
                      {ay.academic_year}
                    </Option>
                  ))}
                </Select>
              </Space>
            }
            style={{
              borderRadius: 16,
              marginBottom: 24,
              background: 'var(--panel-color, #ffffff)',
              borderColor: 'var(--border-color, rgba(0,0,0,0.08))'
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={6}>
                <Card
                  hoverable
                  onClick={() => onMenuSelect && onMenuSelect('live-sessions')}
                  style={{ borderRadius: 16, background: 'var(--panel-color, #ffffff)', borderColor: 'var(--border-color)' }}
                >
                  <Statistic
                    title={<span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Active Classrooms</span>}
                    value={activeCount}
                    prefix={<VideoCameraOutlined style={{ marginRight: 8, color: activeCount > 0 ? '#ef4444' : '#60a5fa' }} />}
                    valueStyle={{ color: activeCount > 0 ? '#ef4444' : '#60a5fa', fontWeight: 700 }}
                  />
                  <div style={{ marginTop: 12 }}>
                    <Badge
                      status={activeCount > 0 ? 'processing' : 'default'}
                      text={<span style={{ color: 'var(--text-color)' }}>{activeCount > 0 ? '🔴 Live Now' : 'No Live Now'}</span>}
                    />
                  </div>
                </Card>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Card
                  hoverable
                  onClick={() => onMenuSelect && onMenuSelect('courses')}
                  style={{ borderRadius: 16, background: 'var(--panel-color, #ffffff)', borderColor: 'var(--border-color)' }}
                >
                  <Statistic
                    title={<span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Enrolled Courses</span>}
                    value={summary.totalCourses || 0}
                    prefix={<BookOutlined style={{ marginRight: 8, color: '#10b981' }} />}
                    valueStyle={{ color: '#10b981', fontWeight: 700 }}
                  />
                  <div style={{ marginTop: 12, color: 'var(--text-secondary)', fontSize: 12 }}>Academic subjects</div>
                </Card>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Card
                  hoverable
                  onClick={() => onMenuSelect && onMenuSelect('courses')}
                  style={{ borderRadius: 16, background: 'var(--panel-color, #ffffff)', borderColor: 'var(--border-color)' }}
                >
                  <Statistic
                    title={<span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Study Modules</span>}
                    value={summary.totalModules || 0}
                    prefix={<DesktopOutlined style={{ marginRight: 8, color: '#f59e0b' }} />}
                    valueStyle={{ color: '#f59e0b', fontWeight: 700 }}
                  />
                  <div style={{ marginTop: 12, color: 'var(--text-secondary)', fontSize: 12 }}>Chapter units</div>
                </Card>
              </Col>

              <Col xs={24} sm={12} lg={6}>
                <Card
                  hoverable
                  onClick={() => onMenuSelect && onMenuSelect('courses')}
                  style={{ borderRadius: 16, background: 'var(--panel-color, #ffffff)', borderColor: 'var(--border-color)' }}
                >
                  <Statistic
                    title={<span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Recorded Lectures</span>}
                    value={summary.totalVideoIndex || 0}
                    prefix={<ReadOutlined style={{ marginRight: 8, color: '#8b5cf6' }} />}
                    valueStyle={{ color: '#8b5cf6', fontWeight: 700 }}
                  />
                  <div style={{ marginTop: 12, color: 'var(--text-secondary)', fontSize: 12 }}>VOD study materials</div>
                </Card>
              </Col>
            </Row>
          </Card>

          {/* SCHEDULED ASSESSMENTS & QUIZZES (15 MINS PRIOR TO START TIME) */}
          {scheduledAssessments && scheduledAssessments.length > 0 && (
            <Card
              title={
                <Space>
                  <ThunderboltOutlined style={{ fontSize: 18, color: '#f59e0b' }} />
                  <Text strong style={{ fontSize: 16, color: 'var(--text-color, #374151)' }}>
                    Scheduled Assessments & Quizzes
                  </Text>
                  <Badge
                    count={scheduledAssessments.length}
                    style={{ backgroundColor: '#3b82f6', marginLeft: 8 }}
                  />
                </Space>
              }
              extra={
                <Text style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  Appears 15 mins before scheduled start time
                </Text>
              }
              style={{
                borderRadius: 16,
                marginBottom: 24,
                background: 'var(--panel-color, #ffffff)',
                borderColor: 'var(--border-color, rgba(0,0,0,0.08))',
                boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
              }}
            >
              <Row gutter={[16, 16]}>
                {scheduledAssessments.map((quiz) => {
                  const now = dayjs();
                  const start = quiz.start_time ? dayjs(quiz.start_time) : null;
                  const end = quiz.end_time ? dayjs(quiz.end_time) : null;
                  const isUpcoming = start && now.isBefore(start);
                  const isLive = start ? (now.isAfter(start) && (!end || now.isBefore(end))) : true;
                  const hasAttempted = quiz.my_attempts_count > 0;
                  const allowMultiple = Boolean(quiz.allow_multiple_attempts);

                  // Calculate countdown minutes/seconds
                  let countdownText = '';
                  if (isUpcoming && start) {
                    const diffMins = start.diff(now, 'minute');
                    const diffSecs = start.diff(now, 'second') % 60;
                    countdownText = `Starts in ${diffMins > 0 ? `${diffMins}m ` : ''}${diffSecs}s`;
                  } else if (isLive && end) {
                    const diffMins = end.diff(now, 'minute');
                    countdownText = `Closes in ${diffMins} mins`;
                  }

                  return (
                    <Col xs={24} md={12} lg={8} key={quiz.assessment_id}>
                      <Card
                        size="small"
                        style={{
                          borderRadius: 12,
                          background: isLive ? 'rgba(59, 130, 246, 0.04)' : 'var(--panel-color, #ffffff)',
                          border: isLive ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid var(--border-color, #e2e8f0)',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between'
                        }}
                      >
                        <div>
                          {/* Course & Module tags */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <Space size={4} wrap>
                              <Tag color="blue" style={{ fontSize: 11, borderRadius: 6 }}>
                                {quiz.courses_name}
                              </Tag>
                              <Tag color="cyan" style={{ fontSize: 11, borderRadius: 6 }}>
                                {quiz.modules_name}
                              </Tag>
                            </Space>

                            {isUpcoming ? (
                              <Tag color="orange" style={{ fontWeight: 600, borderRadius: 6 }}>
                                ⏳ {countdownText}
                              </Tag>
                            ) : isLive ? (
                              <Tag color="green" style={{ fontWeight: 600, borderRadius: 6 }}>
                                🟢 Active Now
                              </Tag>
                            ) : (
                              <Tag color="default">Ended</Tag>
                            )}
                          </div>

                          {/* Assessment Title & Description */}
                          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-color, #1e293b)', marginBottom: 4 }}>
                            {quiz.title}
                          </div>
                          {quiz.description && (
                            <Paragraph
                              ellipsis={{ rows: 2 }}
                              style={{ fontSize: 12, color: 'var(--text-secondary, #64748b)', marginBottom: 10 }}
                            >
                              {quiz.description}
                            </Paragraph>
                          )}

                          {/* Meta Details */}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
                            <span>
                              <FileTextOutlined style={{ marginRight: 4, color: '#8b5cf6' }} />
                              {quiz.total_questions} Questions
                            </span>
                            <span>
                              <ClockCircleOutlined style={{ marginRight: 4, color: '#f59e0b' }} />
                              {quiz.duration_minutes > 0 ? `${quiz.duration_minutes} Mins` : 'No Limit'}
                            </span>
                            {start && (
                              <span>
                                🕒 {start.format('hh:mm A')} - {end ? end.format('hh:mm A') : 'End of day'}
                              </span>
                            )}
                          </div>

                          {/* Score if already attempted */}
                          {hasAttempted && (
                            <div style={{ marginBottom: 12, padding: '6px 10px', background: 'rgba(16, 185, 129, 0.08)', borderRadius: 8, border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                              <Text style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>
                                <CheckCircleOutlined style={{ marginRight: 6 }} />
                                Last Attempt Score: {quiz.last_score ?? 0} pts ({quiz.last_percentage ?? 0}%)
                              </Text>
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                          {hasAttempted && (
                            <Button
                              type="default"
                              size="middle"
                              icon={<EyeOutlined />}
                              onClick={() => handleOpenResults(quiz)}
                              style={{ flex: 1, borderRadius: 8 }}
                            >
                              View My Result
                            </Button>
                          )}

                          {(!hasAttempted || allowMultiple) && (
                            <Button
                              type="primary"
                              size="middle"
                              icon={<RightOutlined />}
                              disabled={isUpcoming}
                              onClick={() => handleStartQuiz(quiz)}
                              style={{
                                flex: 1,
                                borderRadius: 8,
                                background: isLive ? '#2563eb' : undefined,
                                borderColor: isLive ? '#2563eb' : undefined
                              }}
                            >
                              {isUpcoming ? 'Starts Soon' : hasAttempted ? 'Retake Test' : 'Start Assessment'}
                            </Button>
                          )}
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </Card>
          )}

          {/* Watch Analytics Section */}
          {watchAnalytics && (
            <Card
              title={
                <Text strong style={{ fontSize: 16, color: 'var(--text-color, #374151)' }}>
                  Your Watch Analytics
                </Text>
              }
              style={{
                borderRadius: 16,
                background: 'var(--panel-color, #ffffff)',
                borderColor: 'var(--border-color, rgba(0,0,0,0.08))',
                marginBottom: 24
              }}
            >
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={12} sm={6}>
                  <Card size="small" style={{ background: 'var(--panel-color, #ffffff)', borderColor: 'var(--border-color, #e2e8f0)', textAlign: 'center' }}>
                    <Statistic 
                      title={<span style={{ color: 'var(--text-secondary)' }}>Unique Videos</span>}
                      value={watchAnalytics.summary.unique_videos_watched} 
                      valueStyle={{ color: 'var(--text-color)' }}
                      prefix={<PlaySquareOutlined style={{color: '#3b82f6'}}/>}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card size="small" style={{ background: 'var(--panel-color, #ffffff)', borderColor: 'var(--border-color, #e2e8f0)', textAlign: 'center' }}>
                    <Statistic 
                      title={<span style={{ color: 'var(--text-secondary)' }}>Total Duration Watched</span>}
                      value={watchAnalytics.summary.total_watched_formatted} 
                      valueStyle={{ color: 'var(--text-color)' }}
                      prefix={<FieldTimeOutlined style={{color: '#10b981'}}/>}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card size="small" style={{ background: 'var(--panel-color, #ffffff)', borderColor: 'var(--border-color, #e2e8f0)', textAlign: 'center' }}>
                    <Statistic 
                      title={<span style={{ color: 'var(--text-secondary)' }}>Available Video Index</span>}
                      value={watchAnalytics.summary.total_available_formatted} 
                      valueStyle={{ color: 'var(--text-color)' }}
                      prefix={<BarChartOutlined style={{color: '#8b5cf6'}}/>}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card size="small" style={{ background: 'var(--panel-color, #ffffff)', borderColor: 'var(--border-color, #e2e8f0)', textAlign: 'center' }}>
                    <Statistic 
                      title={<span style={{ color: 'var(--text-secondary)' }}>Completion</span>}
                      value={watchAnalytics.summary.percentage_watched} 
                      valueStyle={{ color: 'var(--text-color)' }}
                      suffix="%"
                      prefix={<PieChartOutlined style={{color: '#f59e0b'}}/>}
                    />
                  </Card>
                </Col>
              </Row>
              <CourseWatchChart records={watchAnalytics.records || []} />
            </Card>
          )}

          {/* QUIZ PLAYER MODAL */}
          <Modal
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginRight: 24 }}>
                <Space>
                  <FileTextOutlined style={{ color: '#3b82f6' }} />
                  <span style={{ fontWeight: 700 }}>{activeQuiz?.title}</span>
                </Space>
                {timeLeftSeconds !== null && !quizResult && (
                  <Tag
                    color={timeLeftSeconds < 120 ? 'red' : 'orange'}
                    style={{ fontSize: 14, padding: '4px 12px', borderRadius: 8, fontWeight: 700 }}
                  >
                    <ClockCircleOutlined style={{ marginRight: 6 }} />
                    {Math.floor(timeLeftSeconds / 60)}:
                    {String(timeLeftSeconds % 60).padStart(2, '0')}
                  </Tag>
                )}
              </div>
            }
            open={playerVisible}
            onCancel={() => {
              if (quizResult) {
                setPlayerVisible(false);
              } else {
                Modal.confirm({
                  title: 'Exit Assessment?',
                  content: 'Your answers so far will not be submitted if you exit.',
                  okText: 'Exit Quiz',
                  okType: 'danger',
                  cancelText: 'Stay',
                  onOk: () => setPlayerVisible(false)
                });
              }
            }}
            footer={null}
            width={800}
            destroyOnClose
          >
            {quizResult ? (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <TrophyOutlined style={{ fontSize: 64, color: '#f59e0b', marginBottom: 16 }} />
                <Title level={3}>Assessment Completed!</Title>
                <div style={{ margin: '20px 0' }}>
                  <Progress
                    type="circle"
                    percent={quizResult.percentage}
                    status={quizResult.percentage >= 50 ? 'success' : 'exception'}
                    width={140}
                  />
                </div>
                <Paragraph style={{ fontSize: 16 }}>
                  You scored <strong>{quizResult.score}</strong> out of <strong>{quizResult.max_possible_points}</strong> points
                  ({quizResult.correct_count} correct / {quizResult.total_questions} questions).
                </Paragraph>
                <Divider />
                <div style={{ textAlign: 'left', maxHeight: 350, overflowY: 'auto' }}>
                  <Text strong style={{ color: 'var(--text-color)' }}>Questions Summary:</Text>
                  {(quizResult.breakdown || []).map((b, idx) => (
                    <Card
                      key={idx}
                      size="small"
                      style={{
                        marginTop: 10,
                        borderLeft: `4px solid ${b.is_correct ? '#10b981' : '#ef4444'}`,
                        borderColor: b.is_correct ? 'rgba(16, 185, 129, 0.35)' : 'rgba(239, 68, 68, 0.35)',
                        background: b.is_correct ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                        borderRadius: 8
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Text strong style={{ color: 'var(--text-color)' }}>Q{idx + 1}: {b.question_text}</Text>
                        <Tag color={b.is_correct ? 'green' : 'red'}>
                          {b.is_correct ? <CheckCircleOutlined /> : <CloseCircleOutlined />} {b.marks_earned > 0 ? `+${b.marks_earned} pts` : `${b.marks_earned} pts`}
                        </Tag>
                      </div>
                      <div style={{ marginTop: 6, fontSize: 13 }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Your Answer: </span>
                        <strong style={{ color: 'var(--text-color)' }}>{b.options?.[parseInt(b.user_answer)] || b.user_answer || 'No Answer'}</strong>
                      </div>
                    </Card>
                  ))}
                </div>
                <Button
                  type="primary"
                  size="large"
                  onClick={() => setPlayerVisible(false)}
                  style={{ marginTop: 24 }}
                >
                  Close & Back to Dashboard
                </Button>
              </div>
            ) : (
              <div>
                {quizQuestions.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Text type="secondary">No questions found in this assessment.</Text>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
                      <Text type="secondary">
                        Question <strong>{currentQIndex + 1}</strong> of <strong>{quizQuestions.length}</strong>
                      </Text>
                      <Text strong style={{ color: '#2563eb' }}>
                        Points: {quizQuestions[currentQIndex]?.points || 1} pts
                        {quizQuestions[currentQIndex]?.negative_points > 0 && ` (Neg: -${quizQuestions[currentQIndex].negative_points})`}
                      </Text>
                    </div>

                    <Progress
                      percent={Math.round(((currentQIndex + 1) / quizQuestions.length) * 100)}
                      showInfo={false}
                      style={{ marginBottom: 20 }}
                    />

                    {(() => {
                      const currentQ = quizQuestions[currentQIndex];
                      if (!currentQ) return null;

                      return (
                        <Card style={{ marginBottom: 20, background: 'var(--panel-color, rgba(255, 255, 255, 0.03))', borderColor: 'var(--border-color, rgba(255, 255, 255, 0.12))', borderRadius: 12 }}>
                          <Title level={4} style={{ marginBottom: 16, color: 'var(--text-color)' }}>
                            {currentQ.question_text}
                          </Title>

                          {currentQ.question_image && (
                            <div style={{ textAlign: 'center', marginBottom: 16 }}>
                              <img
                                src={getFullImageUrl(currentQ.question_image)}
                                alt="Question"
                                style={{ maxHeight: 220, maxWidth: '100%', borderRadius: 8, border: '1px solid var(--border-color, rgba(255, 255, 255, 0.15))' }}
                              />
                            </div>
                          )}

                          <Radio.Group
                            style={{ width: '100%' }}
                            value={userAnswers[currentQ.question_id]}
                            onChange={(e) => setUserAnswers(prev => ({ ...prev, [currentQ.question_id]: e.target.value }))}
                          >
                            <Space direction="vertical" style={{ width: '100%' }}>
                              {(currentQ.options || []).map((optText, optIdx) => (
                                <Radio
                                  key={optIdx}
                                  value={String(optIdx)}
                                  style={{
                                    display: 'flex',
                                    width: '100%',
                                    padding: '10px 14px',
                                    border: userAnswers[currentQ.question_id] === String(optIdx) ? '1px solid #3b82f6' : '1px solid var(--border-color, rgba(255, 255, 255, 0.15))',
                                    borderRadius: 8,
                                    marginBottom: 6,
                                    background: userAnswers[currentQ.question_id] === String(optIdx) ? 'rgba(59, 130, 246, 0.15)' : 'var(--panel-color, rgba(255, 255, 255, 0.02))',
                                    color: 'var(--text-color)'
                                  }}
                                >
                                  <strong>{String.fromCharCode(65 + optIdx)}:</strong> {optText}
                                </Radio>
                              ))}
                            </Space>
                          </Radio.Group>
                        </Card>
                      );
                    })()}

                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Button
                        disabled={currentQIndex === 0}
                        onClick={() => setCurrentQIndex(prev => prev - 1)}
                      >
                        Previous Question
                      </Button>

                      {currentQIndex < quizQuestions.length - 1 ? (
                        <Button type="primary" onClick={() => setCurrentQIndex(prev => prev + 1)}>
                          Next Question
                        </Button>
                      ) : (
                        <Button
                          type="primary"
                          danger
                          loading={submittingQuiz}
                          onClick={handleFinalSubmitQuiz}
                        >
                          Submit Assessment
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Modal>

          {/* MY ATTEMPT RESULTS MODAL */}
          <Modal
            title={
              <Space>
                <TrophyOutlined style={{ color: '#f59e0b' }} />
                <span>My Attempt Results: {selectedAssessmentForResults?.title}</span>
              </Space>
            }
            open={resultsVisible}
            onCancel={() => setResultsVisible(false)}
            footer={[<Button key="close" type="primary" onClick={() => setResultsVisible(false)}>Close</Button>]}
            width={850}
            destroyOnClose
          >
            {loadingResults ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin tip="Loading attempt results..." /></div>
            ) : resultsList.length === 0 ? (
              <Empty description="No attempt records found for this assessment." />
            ) : (
              <div>
                {resultsList.map((att, idx) => (
                  <Card
                    key={att.attempt_id || idx}
                    size="small"
                    style={{
                      marginBottom: 16,
                      borderRadius: 12,
                      background: 'var(--panel-color, #ffffff)',
                      borderColor: 'var(--border-color, rgba(255, 255, 255, 0.12))'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-color)' }}>
                          {att.full_name || att.student_full_name || att.login_name || 'Student'}
                        </div>
                        {att.login_username && (
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                            @{att.login_username}
                          </div>
                        )}
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                          Attempt #{resultsList.length - idx} • Submitted on {dayjs(att.attempted_at).format('DD MMM YYYY, hh:mm A')}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <Tag color={att.percentage >= 50 ? 'green' : 'red'} style={{ fontSize: 14, padding: '4px 10px', fontWeight: 700 }}>
                          {att.percentage}% ({att.correct_count} / {att.total_questions} Correct)
                        </Tag>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                          Total Score: {att.score} pts
                        </div>
                      </div>
                    </div>

                    <Divider style={{ margin: '8px 0', borderColor: 'var(--border-color, rgba(255, 255, 255, 0.08))' }} />

                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-color)' }}>Responses Breakdown:</div>
                    {(att.breakdown || []).map((b, qIdx) => (
                      <div
                        key={qIdx}
                        style={{
                          padding: '10px 14px',
                          marginBottom: 8,
                          borderRadius: 8,
                          background: b.is_correct ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                          border: `1px solid ${b.is_correct ? 'rgba(16, 185, 129, 0.25)' : 'rgba(239, 68, 68, 0.25)'}`,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600, color: 'var(--text-color)' }}>
                            Q{qIdx + 1}: {b.question_text}
                          </div>
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>
                            Your Choice: <strong style={{ color: 'var(--text-color)' }}>{b.options?.[parseInt(b.user_answer)] || b.user_answer || 'No Answer'}</strong>
                          </div>
                        </div>
                        <Tag color={b.is_correct ? 'green' : 'red'}>
                          {b.is_correct ? <CheckCircleOutlined /> : <CloseCircleOutlined />} {b.marks_earned > 0 ? `+${b.marks_earned} pts` : `${b.marks_earned} pts`}
                        </Tag>
                      </div>
                    ))}
                  </Card>
                ))}
              </div>
            )}
          </Modal>
        </>
      )}
    </div>
  );
};

export default UserDashboard;

